import type { DifficultyKey } from "@bastion/data";
import type { GameCommand, GameMode, GameStatus } from "./types";

export const REPLAY_SCHEMA_VERSION = 2;
export const REPLAY_WIRE_SCHEMA_VERSION = 3;

export interface ReplayRecordingHeader {
  schemaVersion: number;
  mapId: string;
  seed: number;
  difficulty: DifficultyKey;
  mode: GameMode;
  stepSeconds: number;
  contentFingerprint: string;
}

export interface ReplayStartEntry {
  kind: "start";
  order: number;
}

export interface ReplayRestartEntry {
  kind: "restart";
  order: number;
}

export interface ReplayCommandEntry {
  kind: "command";
  order: number;
  command: GameCommand;
  /** false 表示该命令在原局中被核心拒绝。 */
  accepted: boolean;
}

export interface ReplayAdvanceEntry {
  kind: "advance";
  order: number;
  /** 原局单次 advanceTo 收到的绝对模拟目标时间（本局时钟）。 */
  targetTime: number;
  /** 该调用实际请求推进的模拟增量，用于全局回放时间轴。 */
  deltaSeconds: number;
}

export type ReplayEntry =
  | ReplayStartEntry
  | ReplayRestartEntry
  | ReplayCommandEntry
  | ReplayAdvanceEntry;

interface ReplayAdvanceBatchEntry {
  kind: "advance_batch";
  order: number;
  advances: Array<[targetTime: number, deltaSeconds: number]>;
}

type ReplayWireEntry =
  | Exclude<ReplayEntry, ReplayAdvanceEntry>
  | ReplayAdvanceEntry
  | ReplayAdvanceBatchEntry;

interface ReplayWireFile extends Omit<ReplayFile, "schemaVersion" | "entries"> {
  schemaVersion: typeof REPLAY_WIRE_SCHEMA_VERSION;
  entries: ReplayWireEntry[];
}

export interface ReplayOutcome {
  status: Extract<GameStatus, "won" | "lost">;
  wave: number;
  completedAt: number;
}

export interface ReplayFile extends ReplayRecordingHeader {
  durationSeconds: number;
  entries: ReplayEntry[];
  outcome?: ReplayOutcome;
}

export function parseReplayFile(source: string): ReplayFile {
  let value: unknown;
  try {
    value = JSON.parse(source) as unknown;
  } catch {
    throw new Error("文件不是有效的 JSON 格式");
  }
  if (!isRecord(value)) throw new Error("文件不是受支持的战斗回放");
  if (value.schemaVersion === REPLAY_SCHEMA_VERSION) {
    if (!isReplayFile(value)) throw new Error("文件不是受支持的战斗回放");
    return value;
  }
  if (value.schemaVersion !== REPLAY_WIRE_SCHEMA_VERSION) {
    throw new Error("文件不是受支持的战斗回放");
  }
  const normalized = expandReplayWireFile(value);
  if (!isReplayFile(normalized)) throw new Error("文件不是受支持的战斗回放");
  return normalized;
}

export function serializeReplayFile(replay: ReplayFile): string {
  if (!isReplayFile(replay)) throw new Error("战斗回放数据无效");
  return JSON.stringify(compactReplayFile(replay));
}

function compactReplayFile(replay: ReplayFile): ReplayWireFile {
  const entries: ReplayWireEntry[] = [];
  let index = 0;
  while (index < replay.entries.length) {
    const entry = replay.entries[index];
    if (entry.kind !== "advance") {
      entries.push(entry);
      index += 1;
      continue;
    }

    const advances: Array<[targetTime: number, deltaSeconds: number]> = [];
    const order = entry.order;
    while (index < replay.entries.length && replay.entries[index].kind === "advance") {
      const advance = replay.entries[index];
      if (advance.kind !== "advance") break;
      advances.push([advance.targetTime, advance.deltaSeconds]);
      index += 1;
    }
    if (advances.length === 1) {
      const [targetTime, deltaSeconds] = advances[0];
      entries.push({ kind: "advance", order, targetTime, deltaSeconds });
    } else {
      entries.push({ kind: "advance_batch", order, advances });
    }
  }
  return { ...replay, schemaVersion: REPLAY_WIRE_SCHEMA_VERSION, entries };
}

function expandReplayWireFile(value: Record<string, unknown>): unknown {
  if (!Array.isArray(value.entries)) return value;
  const entries: unknown[] = [];
  for (const entry of value.entries) {
    if (!isRecord(entry) || entry.kind !== "advance_batch") {
      entries.push(entry);
      continue;
    }
    if (
      !isNonNegativeInteger(entry.order) ||
      !Array.isArray(entry.advances) ||
      entry.advances.length < 2
    ) {
      return null;
    }
    for (let index = 0; index < entry.advances.length; index += 1) {
      const advance = entry.advances[index];
      if (
        !Array.isArray(advance) ||
        advance.length !== 2 ||
        !isNonNegativeNumber(advance[0]) ||
        !isNonNegativeNumber(advance[1])
      ) {
        return null;
      }
      entries.push({
        kind: "advance",
        order: entry.order + index,
        targetTime: advance[0],
        deltaSeconds: advance[1],
      });
    }
  }
  return { ...value, schemaVersion: REPLAY_SCHEMA_VERSION, entries };
}

export function isReplayFile(value: unknown): value is ReplayFile {
  if (!isRecord(value)) return false;
  if (
    value.schemaVersion !== REPLAY_SCHEMA_VERSION ||
    typeof value.mapId !== "string" ||
    !value.mapId ||
    !isFiniteNumber(value.seed) ||
    !isDifficulty(value.difficulty) ||
    (value.mode !== "normal" && value.mode !== "debug") ||
    !isPositiveNumber(value.stepSeconds) ||
    typeof value.contentFingerprint !== "string" ||
    !value.contentFingerprint ||
    !isNonNegativeNumber(value.durationSeconds) ||
    !Array.isArray(value.entries)
  ) {
    return false;
  }

  let totalSeconds = 0;
  let sawStart = false;
  let pendingRestart = false;
  for (let index = 0; index < value.entries.length; index += 1) {
    const entry = value.entries[index];
    if (!isReplayEntry(entry) || entry.order !== index) return false;
    if (entry.kind === "start") {
      if (sawStart || index !== 0 || pendingRestart) return false;
      sawStart = true;
      continue;
    }
    if (entry.kind === "restart") {
      if (!sawStart || pendingRestart) return false;
      pendingRestart = true;
      continue;
    }
    if (entry.kind === "advance") {
      if (!sawStart) return false;
      totalSeconds += entry.deltaSeconds;
      pendingRestart = false;
      continue;
    }
    if (!sawStart) return false;
    if (entry.command.type === "restart_game") return false;
    pendingRestart = false;
  }
  if (!sawStart || Math.abs(totalSeconds - value.durationSeconds) > 1e-6) return false;

  if (value.outcome !== undefined) {
    if (
      !isRecord(value.outcome) ||
      (value.outcome.status !== "won" && value.outcome.status !== "lost") ||
      !isNonNegativeInteger(value.outcome.wave) ||
      !isNonNegativeNumber(value.outcome.completedAt) ||
      Math.abs(value.outcome.completedAt - totalSeconds) > 1e-6
    ) {
      return false;
    }
  }
  return true;
}

export function isReplayEntry(value: unknown): value is ReplayEntry {
  if (!isRecord(value) || !isNonNegativeInteger(value.order)) return false;
  if (value.kind === "start" || value.kind === "restart") return true;
  if (value.kind === "advance") {
    return isNonNegativeNumber(value.targetTime) && isNonNegativeNumber(value.deltaSeconds);
  }
  return (
    value.kind === "command" && typeof value.accepted === "boolean" && isGameCommand(value.command)
  );
}

export function cloneCommand(command: GameCommand): GameCommand {
  return JSON.parse(JSON.stringify(command)) as GameCommand;
}

function isGameCommand(value: unknown): value is GameCommand {
  if (!isRecord(value) || typeof value.type !== "string") return false;
  switch (value.type) {
    case "build_tower":
      return typeof value.towerType === "string" && isPoint(value.position);
    case "build_enemy_debug":
      return typeof value.enemyType === "string" && isPoint(value.position);
    case "upgrade_tower":
    case "sell_tower":
      return typeof value.towerId === "string";
    case "start_wave":
    case "pause_game":
    case "resume_game":
    case "restart_game":
      return true;
    case "set_game_speed":
      return value.speed === 1 || value.speed === 2 || value.speed === 4;
    case "use_skill":
      return (
        typeof value.skillId === "string" &&
        (value.position === undefined || isPoint(value.position))
      );
    case "start_game":
      return value.seed === undefined || isFiniteNumber(value.seed);
    default:
      return false;
  }
}

function isPoint(value: unknown): boolean {
  return isRecord(value) && isFiniteNumber(value.x) && isFiniteNumber(value.y);
}

function isDifficulty(value: unknown): value is DifficultyKey {
  return value === "easy" || value === "normal" || value === "hard";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isNonNegativeNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value >= 0;
}

function isPositiveNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value > 0;
}

function isNonNegativeInteger(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 0;
}
